/**
 * Parses a JWT payload without signature verification.
 * Returns null when token is missing or malformed.
 */
function parseJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function toNormalizedRoleSet(roles: string[]): Set<string> {
  return new Set(
    roles
      .map((r) => r.trim().toLowerCase())
      .filter((r) => r.length > 0)
  );
}

/**
 * Gets a user's role names from extension_Roles claim (comma-separated).
 * Strips optional "~TenantId" suffix so "RoleName~TenantId" is returned as "RoleName".
 */
export function getExtensionRolesFromToken(token: string | null | undefined): string[] {
  const payload = parseJwtPayload(token);
  const extensionRoles = payload?.extension_Roles;
  if (typeof extensionRoles !== 'string' || extensionRoles.trim().length === 0) {
    return [];
  }

  return extensionRoles
    .split(',')
    .map((r) => {
      const trimmed = r.trim();
      const tildeIndex = trimmed.indexOf('~');
      return tildeIndex >= 0 ? trimmed.slice(0, tildeIndex).trim() : trimmed;
    })
    .filter((r) => r.length > 0);
}

/**
 * Checks if current token has any allowed global role.
 */
export function hasGlobalAccess(token: string | null | undefined, allowedRoles: string[]): boolean {
  if (!token || allowedRoles.length === 0) return false;

  const userRoles = toNormalizedRoleSet(getExtensionRolesFromToken(token));
  if (userRoles.size === 0) return false;

  return allowedRoles.some((role) => userRoles.has(role.trim().toLowerCase()));
}
